import { Routes } from "@angular/router";
import { adminGuard } from "./core/guards/admin.guard";
import { authGuard } from "./core/guards/auth.guard";
import { instructorGuard } from "./core/guards/instructor.guard";

// Lazy-loaded placeholder — replaced per-subplan as each page is implemented
const placeholder = () =>
  import("./shared/components/placeholder/placeholder.component").then(
    (m) => m.PlaceholderComponent,
  );

// Authenticated shell — replaced in SUB-S01/SUB-I01
const appShell = () =>
  import("./layout/app-shell/app-shell.component").then(
    (m) => m.AppShellComponent,
  );

export const APP_ROUTES: Routes = [
  // ── PUBLIC ────────────────────────────────────────────────

  {
    // SUB-P01 — Landing Page
    path: "",
    pathMatch: "full",
    loadComponent: () =>
      import("./features/landing/landing-page.component").then(
        (m) => m.LandingPageComponent,
      ),
    title: "BalizaHub — Encontre seu instrutor de direção",
  },
  {
    // SUB-P02 — Search Results (public, SEO-indexable)
    path: "busca",
    loadComponent: () =>
      import("./features/search/search-page.component").then(
        (m) => m.SearchPageComponent,
      ),
    title: "Buscar Instrutores — BalizaHub",
  },
  {
    // SUB-P03 — Instructor Public Profile
    path: "instrutor/:id",
    loadComponent: () =>
      import("./features/instructor-profile/instructor-profile-page.component").then(
        (m) => m.InstructorProfilePageComponent,
      ),
    title: "Perfil do Instrutor — BalizaHub",
  },

  // ── AUTH ──────────────────────────────────────────────────

  {
    // SUB-P04 — Login
    path: "entrar",
    loadComponent: () =>
      import("./features/auth/login/login.component").then(
        (m) => m.LoginComponent,
      ),
    title: "Entrar — BalizaHub",
  },
  {
    // SUB-P04 — Register (multi-step wizard: email → role → dados → confirmação)
    path: "cadastro",
    loadComponent: () =>
      import("./features/auth/register/register.component").then(
        (m) => m.RegisterComponent,
      ),
    title: "Criar conta — BalizaHub",
  },
  {
    // SUB-P04 — Forgot password
    path: "recuperar-senha",
    loadComponent: () =>
      import("./features/auth/forgot-password/forgot-password.component").then(
        (m) => m.ForgotPasswordComponent,
      ),
    title: "Recuperar Senha — BalizaHub",
  },
  {
    path: "redefinir-senha/:token",
    loadComponent: () =>
      import("./features/auth/reset-password/reset-password.component").then(
        (m) => m.ResetPasswordComponent,
      ),
    title: "Redefinir Senha — BalizaHub",
  },

  // ── AUTHENTICATED APP SHELL ───────────────────────────────
  // All /app/* routes live inside the authenticated shell.

  {
    path: "app",
    loadComponent: appShell,
    canActivate: [authGuard],
    children: [
      // ── DASHBOARD ────────────────────────────────────────
      {
        // SUB-S01 — Student Dashboard
        path: "",
        pathMatch: "full",
        loadComponent: () =>
          import("./features/dashboard/student/student-dashboard-page.component").then(
            (m) => m.StudentDashboardPageComponent,
          ),
        title: "Início — BalizaHub",
      },

      // ── SEARCH (in-app) ──────────────────────────────────
      {
        // Students search for available instructors/slots
        path: "busca",
        loadComponent: placeholder,
        title: "Buscar — BalizaHub",
      },

      // ── BOOKINGS ─────────────────────────────────────────
      {
        // SUB-S03 — My Bookings list (student & instructor)
        path: "agendamentos",
        loadComponent: () =>
          import("./features/bookings/shared/my-bookings/my-bookings-page.component").then(
            (m) => m.MyBookingsPageComponent,
          ),
        title: "Meus Agendamentos — BalizaHub",
      },
      {
        // SUB-S03 — Booking Detail + contextual actions
        path: "agendamentos/:id",
        loadComponent: () =>
          import("./features/bookings/shared/my-bookings/booking-detail-page.component").then(
            (m) => m.BookingDetailPageComponent,
          ),
        title: "Detalhes do Agendamento — BalizaHub",
      },
      {
        // SUB-S02 — Booking Confirmation Flow (slot → payment instruction → success)
        path: "agendar/:slotId",
        loadComponent: () =>
          import("./features/bookings/student/booking-flow/student-booking-flow-page.component").then(
            (m) => m.StudentBookingFlowPageComponent,
          ),
        title: "Confirmar Agendamento — BalizaHub",
      },

      // ── REVIEW ───────────────────────────────────────────
      {
        // SUB-S05 — Leave a review after a COMPLETED booking
        path: "avaliar/:bookingId",
        loadComponent: () =>
          import("./features/reviews/student/review-flow-page.component").then(
            (m) => m.ReviewFlowPageComponent,
          ),
        title: "Avaliar Aula — BalizaHub",
      },

      // ── STUDENT PROGRESS ─────────────────────────────────
      {
        // SUB-S04 — Progress timeline + badge collection
        path: "progresso",
        loadComponent: () =>
          import("./features/progress/progress-page.component").then(
            (m) => m.ProgressPageComponent,
          ),
        title: "Meu Progresso — BalizaHub",
      },

      // ── PROFILE ──────────────────────────────────────────
      {
        // SUB-I04 (instructor) — Profile editor
        path: "perfil",
        loadComponent: () =>
          import("./features/instrutor/profile-editor/profile-editor-page.component").then(
            (m) => m.ProfileEditorPageComponent,
          ),
        title: "Meu Perfil — BalizaHub",
      },

      // ── NOTIFICATIONS ────────────────────────────────────
      {
        // SUB-C01 — Notification center
        path: "notificacoes",
        loadComponent: () =>
          import("./features/notifications/notifications-page.component").then(
            (m) => m.NotificationsPageComponent,
          ),
        title: "Notificações — BalizaHub",
      },

      // ── SETTINGS ─────────────────────────────────────────
      {
        // SUB-C02 — Account settings, LGPD, subscription
        path: "configuracoes",
        loadComponent: () =>
          import("./features/settings/settings-page.component").then(
            (m) => m.SettingsPageComponent,
          ),
        title: "Configurações — BalizaHub",
      },

      // ── INSTRUCTOR AREA ──────────────────────────────────
      {
        path: "instrutor",
        children: [
          {
            // SUB-I01 — Instructor Dashboard
            path: "",
            pathMatch: "full",
            loadComponent: () =>
              import("./features/dashboard/instructor/instructor-dashboard-page.component").then(
                (m) => m.InstructorDashboardPageComponent,
              ),
            title: "Painel do Instrutor — BalizaHub",
          },
          {
            // SUB-I02 — KYC onboarding wizard
            // CNH upload → Serpro validation → biometric liveness → review → activated
            path: "onboarding",
            loadComponent: () =>
              import("./features/instrutor/onboarding/onboarding.component").then(
                (m) => m.OnboardingComponent,
              ),
            canActivate: [instructorGuard],
            title: "Verificação — BalizaHub",
          },
          {
            // SUB-I03 — Schedule manager
            // Weekly calendar, bulk create slots, booked vs available
            path: "agenda",
            loadComponent: () =>
              import("./features/instrutor/schedule/schedule-page.component").then(
                (m) => m.SchedulePageComponent,
              ),
            title: "Minha Agenda — BalizaHub",
          },
          {
            // Instructor earnings dashboard
            path: "ganhos",
            loadComponent: placeholder,
            title: "Ganhos — BalizaHub",
          },
        ],
      },

      // ── SUBSCRIPTIONS ────────────────────────────────────
      {
        // Instructor subscription checkout (Asaas)
        // TODO: implement SUB-I01 / billing flow — currently using placeholder
        path: "assinatura",
        loadComponent: placeholder,
        title: "Assinatura — BalizaHub",
      },

      // ── ADMIN ─────────────────────────────────────────────
      {
        path: "admin",
        canActivate: [adminGuard],
        children: [
          {
            path: "",
            pathMatch: "full",
            loadComponent: () =>
              import("./features/admin/admin-panel-page.component").then(
                (m) => m.AdminPanelPageComponent,
              ),
            title: "Admin — BalizaHub",
          },
          {
            path: "users/:id",
            loadComponent: () =>
              import("./features/admin/admin-user-detail-page.component").then(
                (m) => m.AdminUserDetailPageComponent,
              ),
            title: "Admin • Usuario — BalizaHub",
          },
          {
            path: "disputes/:id",
            loadComponent: () =>
              import("./features/admin/admin-dispute-detail-page.component").then(
                (m) => m.AdminDisputeDetailPageComponent,
              ),
            title: "Admin • Disputa — BalizaHub",
          },
          {
            path: "transactions/:id",
            loadComponent: () =>
              import("./features/admin/admin-transaction-detail-page.component").then(
                (m) => m.AdminTransactionDetailPageComponent,
              ),
            title: "Admin • Transacao — BalizaHub",
          },
        ],
      },
    ],
  },

  // ── FALLBACK ──────────────────────────────────────────────
  {
    path: "**",
    redirectTo: "",
  },
];
